import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import BottomSheet from "components/common/BottomSheet";
import StarRating from "components/review/StarRating";
import { useSnackbar } from "contexts/SnackbarContext";
import { createReview, normalizeProductType } from "api/review";
import "assets/scss/pages/ReviewBottomSheet.scss";

const MAX_MEDIA = 5;
// Nginx client_max_body_size ≥ 300MB 기준. 이미지 30MB / 동영상 100MB
const MAX_IMAGE_SIZE = 30 * 1024 * 1024;
const MAX_VIDEO_SIZE = 100 * 1024 * 1024;

// 서버 content_type 화이트리스트와 일치시킨다. (HEIC는 서버 지원 미정)
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-matroska"];
const IMAGE_EXTS = /\.(jpe?g|png|gif|webp)$/i;
const VIDEO_EXTS = /\.(mp4|mov|mkv)$/i;
const ACCEPT_ATTR = [...IMAGE_TYPES, ...VIDEO_TYPES].join(",");

const getFileMediaType = (file) => {
    if (IMAGE_TYPES.includes(file.type)) return "image";
    if (VIDEO_TYPES.includes(file.type)) return "video";
    // 일부 브라우저/OS(특히 iOS Safari)는 file.type이 빈 값으로 오는 경우가 있어 확장자 fallback
    const name = file.name || "";
    if (IMAGE_EXTS.test(name)) return "image";
    if (VIDEO_EXTS.test(name)) return "video";
    return null;
};

// 주문(order)에서 리뷰 작성 가능한 아이템 목록을 추출한다.
// 장바구니 통합결제의 경우 order.items에 여러 상품이 들어있다.
const extractReviewableItems = (order) => {
    if (!order) return [];
    const items = Array.isArray(order.items) ? order.items : [];
    if (items.length === 0) {
        // 레거시/단일 상품 주문: order.product_info만 존재할 수 있음
        const legacyId = order.product_info?.id || order.product_id;
        const legacyType = order.product_type || order.product_info?.product_type;
        if (legacyId && legacyType) {
            return [
                {
                    item_id: order.id,
                    product_id: legacyId,
                    product_type: legacyType,
                    product_name: order.product_info?.name || "",
                    thumbnail: order.product_info?.thumbnail || "",
                    brand: order.product_info?.brand || "",
                    quantity: order.quantity || 1,
                    has_review: !!order.has_review,
                },
            ];
        }
        return [];
    }
    return items
        .filter((it) => !it.has_review && it.product_id && it.product_type)
        .map((it) => ({
            item_id: it.item_id,
            product_id: it.product_id,
            product_type: it.product_type,
            product_name: it.product_name || order.product_info?.name || "",
            thumbnail: it.thumbnail || order.product_info?.thumbnail || "",
            brand: it.brand || order.product_info?.brand || "",
            quantity: it.quantity || 1,
            has_review: !!it.has_review,
        }));
};

const ReviewWriteBottomSheet = ({ isOpen, onClose, order, onSuccess }) => {
    const { showSuccess, showError } = useSnackbar();
    const fileInputRef = useRef(null);

    const [rating, setRating] = useState(5);
    const [content, setContent] = useState("");
    const [media, setMedia] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);

    const reviewableItems = useMemo(() => extractReviewableItems(order), [order]);
    const isMultiItem = reviewableItems.length > 1;

    const selectedItem = useMemo(
        () => reviewableItems.find((it) => it.item_id === selectedItemId) || null,
        [reviewableItems, selectedItemId]
    );

    // 시트가 열리거나 order가 바뀔 때 기본 선택 아이템 세팅
    useEffect(() => {
        if (!isOpen) return;
        setSelectedItemId(reviewableItems[0]?.item_id ?? null);
    }, [isOpen, reviewableItems]);

    const resetForm = useCallback(() => {
        setRating(5);
        setContent("");
        setMedia((prev) => {
            prev.forEach((m) => m.preview && URL.revokeObjectURL(m.preview));
            return [];
        });
    }, []);

    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose, resetForm]);

    const handleContentChange = useCallback((e) => {
        setContent(e.target.value.slice(0, 500));
    }, []);

    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleMediaSelect = useCallback((e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const remainingSlots = MAX_MEDIA - media.length;
        if (files.length > remainingSlots) {
            showError(`미디어는 최대 ${MAX_MEDIA}개까지 추가할 수 있습니다.`);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const unsupported = files.find((f) => !getFileMediaType(f));
        if (unsupported) {
            showError("지원하지 않는 파일 형식입니다. (이미지: jpg/png/webp/gif, 동영상: mp4/mov/mkv)");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const validFiles = files.filter((file) => {
            const mediaType = getFileMediaType(file);
            if (mediaType === "video" && file.size > MAX_VIDEO_SIZE) {
                showError("영상 1개당 최대 100MB까지 업로드 가능합니다.");
                return false;
            }
            if (mediaType === "image" && file.size > MAX_IMAGE_SIZE) {
                showError("이미지 1개당 최대 30MB까지 업로드 가능합니다.");
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) {
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const newMedia = validFiles.map((file) => ({
            file,
            type: getFileMediaType(file),
            preview: URL.createObjectURL(file),
        }));

        setMedia((prev) => [...prev, ...newMedia]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [media, showError]);

    const handleRemoveMedia = useCallback((index) => {
        setMedia((prev) => {
            const removed = prev[index];
            if (removed?.preview) URL.revokeObjectURL(removed.preview);
            return prev.filter((_, i) => i !== index);
        });
    }, []);

    const handleSubmit = useCallback(async () => {
        if (isSubmitting) return;
        if (!selectedItem) {
            showError("리뷰를 작성할 상품을 선택해주세요.");
            return;
        }
        if (rating === 0) {
            showError("별점을 선택해주세요.");
            return;
        }
        if (!content.trim()) {
            showError("리뷰 내용을 입력해주세요.");
            return;
        }

        const productType = normalizeProductType(selectedItem.product_type);
        if (!productType) {
            showError("상품 유형을 확인할 수 없습니다.");
            return;
        }
        if (!selectedItem.product_id) {
            showError("상품 정보가 올바르지 않습니다.");
            return;
        }

        setIsSubmitting(true);
        try {
            const images = media.filter((m) => m.type === "image").map((m) => m.file);
            const videos = media.filter((m) => m.type === "video").map((m) => m.file);
            await createReview({
                product_type: productType,
                product_id: selectedItem.product_id,
                rating,
                content,
                images,
                videos,
            });
            showSuccess("리뷰가 등록되었습니다.");
            resetForm();
            onClose();
            onSuccess?.();
        } catch (error) {
            const msg =
                error.response?.data?.message ||
                error.response?.data?.detail ||
                error.message ||
                "리뷰 등록에 실패했습니다.";
            showError(msg);
        } finally {
            setIsSubmitting(false);
        }
    }, [
        isSubmitting,
        selectedItem,
        rating,
        content,
        media,
        showSuccess,
        showError,
        resetForm,
        onClose,
        onSuccess,
    ]);

    // 선택된 아이템이 없는 경우(=리뷰 작성 가능 아이템이 없음) 안내
    const hasNothingToReview = isOpen && reviewableItems.length === 0;

    return (
        <BottomSheet isOpen={isOpen} onClose={handleClose} title="리뷰 작성">
            <div className="review-bs">
                {hasNothingToReview ? (
                    <div className="review-bs__empty">
                        리뷰를 작성할 수 있는 상품이 없습니다.
                    </div>
                ) : (
                    <>
                        {/* 장바구니 통합결제: 여러 상품이 있을 때 리뷰할 상품 선택 */}
                        {isMultiItem && (
                            <>
                                <div className="review-bs__section">
                                    <label className="review-bs__label">리뷰할 상품 선택</label>
                                    <div className="review-bs__item-selector">
                                        {reviewableItems.map((it) => {
                                            const selected = it.item_id === selectedItemId;
                                            return (
                                                <button
                                                    key={it.item_id}
                                                    type="button"
                                                    className={`review-bs__item${selected ? " review-bs__item--selected" : ""}`}
                                                    onClick={() => setSelectedItemId(it.item_id)}
                                                    aria-pressed={selected}
                                                >
                                                    {it.thumbnail && (
                                                        <img
                                                            src={it.thumbnail}
                                                            alt={it.product_name}
                                                            className="review-bs__item-thumb"
                                                        />
                                                    )}
                                                    <span className="review-bs__item-name">
                                                        {it.brand && `[${it.brand}] `}
                                                        {it.product_name}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="review-bs__divider" />
                            </>
                        )}

                        {/* 단일 상품 정보 표시 */}
                        {!isMultiItem && selectedItem && (
                            <>
                                <div className="review-bs__product">
                                    {selectedItem.thumbnail && (
                                        <img
                                            src={selectedItem.thumbnail}
                                            alt={selectedItem.product_name}
                                            className="review-bs__product-thumb"
                                        />
                                    )}
                                    <div className="review-bs__product-info">
                                        <span className="review-bs__product-name">
                                            {selectedItem.brand && `[${selectedItem.brand}] `}
                                            {selectedItem.product_name}
                                        </span>
                                        <span className="review-bs__product-qty">{selectedItem.quantity}개</span>
                                    </div>
                                </div>
                                <div className="review-bs__divider" />
                            </>
                        )}

                        {/* 별점 */}
                        <div className="review-bs__section">
                            <label className="review-bs__label">별점</label>
                            <div className="review-bs__rating-wrap">
                                <StarRating rating={rating} onChange={setRating} size={24} />
                            </div>
                        </div>

                        <div className="review-bs__divider" />

                        {/* 리뷰 내용 */}
                        <div className="review-bs__section">
                            <label className="review-bs__label">리뷰 내용</label>
                            <textarea
                                className="review-bs__textarea"
                                placeholder="솔직한 리뷰를 남겨주세요."
                                value={content}
                                onChange={handleContentChange}
                                maxLength={500}
                            />
                            <span className={`review-bs__char-count${content.length >= 450 ? " near-limit" : ""}`}>
                                {content.length}/500
                            </span>
                        </div>

                        <div className="review-bs__divider" />

                        {/* 사진/동영상 첨부 */}
                        <div className="review-bs__section">
                            <div className="review-bs__label-row">
                                <label className="review-bs__label">사진/동영상 첨부</label>
                                <span className="review-bs__media-count">{media.length}/{MAX_MEDIA}</span>
                            </div>
                            <div className="review-bs__media-list">
                                {media.length < MAX_MEDIA && (
                                    <div
                                        className="review-bs__upload-btn"
                                        onClick={handleUploadClick}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") handleUploadClick();
                                        }}
                                        tabIndex={0}
                                        role="button"
                                        aria-label="미디어 추가"
                                    >
                                        <span className="review-bs__upload-icon">+</span>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept={ACCEPT_ATTR}
                                            multiple
                                            onChange={handleMediaSelect}
                                        />
                                    </div>
                                )}
                                {media.map((item, index) => (
                                    <div key={index} className="review-bs__media-item">
                                        {item.type === "video" ? (
                                            <>
                                                <video
                                                    src={`${item.preview}#t=0.1`}
                                                    muted
                                                    playsInline
                                                    preload="metadata"
                                                />
                                                <div className="review-bs__video-badge">
                                                    <span>&#9654;</span>
                                                </div>
                                            </>
                                        ) : (
                                            <img src={item.preview} alt={`미디어 ${index + 1}`} />
                                        )}
                                        <button
                                            className="review-bs__media-remove"
                                            onClick={() => handleRemoveMedia(index)}
                                            type="button"
                                            aria-label={`미디어 ${index + 1} 삭제`}
                                        >
                                            &times;
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 등록 버튼 */}
                        <div className="review-bs__submit-wrap">
                            <button
                                className="review-bs__submit-btn"
                                onClick={handleSubmit}
                                disabled={isSubmitting || !selectedItem}
                                type="button"
                            >
                                {isSubmitting ? "등록 중..." : "등록하기"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </BottomSheet>
    );
};

export default ReviewWriteBottomSheet;
